export const YOUTUBE_OAUTH_MESSAGE_TYPE = 'twin-tales-youtube-oauth';

const POPUP_FEATURES = 'width=520,height=720,scrollbars=yes,resizable=yes';

function isDesktopOAuth() {
  return typeof window !== 'undefined' && Boolean(window.twinTales?.onOAuthPopupResult);
}

function oauthPopupWindowName() {
  return `twin-tales-youtube-oauth-${Date.now()}`;
}

function isPopupClosed(popup) {
  if (!popup) return true;
  try {
    return popup.closed;
  } catch {
    return false;
  }
}

/**
 * Open Google OAuth in a popup (or new tab if the browser blocks popups).
 * @param {string} authorizationUrl
 * @returns {Window | null}
 */
export function openYoutubeOAuthWindow(authorizationUrl) {
  let win = null;
  try {
    win = window.open(authorizationUrl, oauthPopupWindowName(), POPUP_FEATURES);
  } catch {
    win = null;
  }
  if (win) {
    try {
      win.focus();
    } catch {
      /* cross-origin */
    }
    return win;
  }
  try {
    win = window.open(authorizationUrl, '_blank');
    if (win) win.focus();
  } catch {
    win = null;
  }
  return win;
}

/**
 * Listen for OAuth completion (postMessage on web, IPC on desktop).
 * @param {{ popupRef: import('react').MutableRefObject<Window | null>, onResult: (payload: { status: string, message: string }) => void, onPopupClosed?: () => void }} opts
 * @returns {() => void} cleanup
 */
export function subscribeYoutubeOAuthResult({ popupRef, onResult, onPopupClosed }) {
  let settled = false;
  const deliver = (data) => {
    if (settled) return;
    if (!data || data.type !== YOUTUBE_OAUTH_MESSAGE_TYPE) return;
    settled = true;
    onResult({
      status: String(data.status || ''),
      message: String(data.message || ''),
    });
  };

  const onMessage = (event) => {
    const data = event.data;
    if (!data || data.type !== YOUTUBE_OAUTH_MESSAGE_TYPE) return;
    const popup = popupRef.current;
    if (popup && event.source && event.source !== popup) return;
    deliver(data);
  };

  window.addEventListener('message', onMessage);

  let cleanupDesktop = () => {};
  if (isDesktopOAuth()) {
    cleanupDesktop = window.twinTales.onOAuthPopupResult((payload) => {
      if (payload?.type && payload.type !== YOUTUBE_OAUTH_MESSAGE_TYPE) return;
      deliver({
        type: YOUTUBE_OAUTH_MESSAGE_TYPE,
        status: payload?.status,
        message: payload?.message,
      });
    });
    if (window.twinTales.onOAuthPopupClosed) {
      const cleanupClosed = window.twinTales.onOAuthPopupClosed(() => {
        if (settled) return;
        onPopupClosed?.();
      });
      const prevCleanup = cleanupDesktop;
      cleanupDesktop = () => {
        prevCleanup();
        cleanupClosed();
      };
    }
  }

  let closedNotified = false;
  let intervalId = null;
  if (!isDesktopOAuth()) {
    intervalId = window.setInterval(() => {
      const popup = popupRef.current;
      if (!popup || closedNotified) return;
      if (!isPopupClosed(popup)) return;
      closedNotified = true;
      if (!settled) onPopupClosed?.();
    }, 400);
  }

  return () => {
    window.removeEventListener('message', onMessage);
    if (intervalId != null) window.clearInterval(intervalId);
    cleanupDesktop();
  };
}
